import {
  Badge,
  Banner,
  BlockStack,
  Box,
  Button,
  Card,
  DescriptionList,
  Divider,
  InlineGrid,
  InlineStack,
  Layout,
  Link,
  Page,
  Text,
} from '@shopify/polaris';
import { DeleteIcon, ProductUnavailableIcon, StatusActiveIcon } from '@shopify/polaris-icons';
import Capacity from 'components/Capacity';
import SkeletonPageLoading from 'components/skeletonPageLoading';
import { useDeleteConfirm } from 'context/DeleteConfirmContext';
import __helpers from 'helpers/index';
import { Helmet } from 'react-helmet-async';
import {
  useGetDriverSettlement,
  useMutateDriverSettlementDelete,
  useMutateDriverSettlementMarkAsClose,
  useMutateDriverSettlementMarkAsOpen,
} from 'queries/driver_settlement.query';
import { ORDER_FEE_LABEL, TRIP_FEE_LABELS } from 'queries/orders.query';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);

const formatMoney = (value?: number | string | null) => `${__helpers.formatNumberWithCommas(Number(value || 0))} vnd`;

const formatDate = (value?: string, includeTime = true) => {
  if (!value) return '--';
  const numericValue = Number(value);
  const date = Number.isNaN(numericValue) ? dayjs(value) : dayjs(numericValue);
  if (!date.isValid()) return '--';
  return date.tz('Asia/Ho_Chi_Minh').format(includeTime ? 'HH:mm DD-MM-YYYY' : 'DD-MM-YYYY');
};

const SectionTitle = ({ children }: { children: string }) => (
  <Text as="h2" variant="headingMd" fontWeight="semibold">
    {children}
  </Text>
);

export default function DriverSettlementView() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { showDeleteConfirm } = useDeleteConfirm();
  const { data: settlement, isLoading, isError, refetch } = useGetDriverSettlement(id);
  const { mutateAsync: markAsClosed, isPending: isClosing } = useMutateDriverSettlementMarkAsClose();
  const { mutateAsync: markAsOpen, isPending: isOpening } = useMutateDriverSettlementMarkAsOpen();
  const { mutateAsync: deleteSettlement, isPending: isDeleting } = useMutateDriverSettlementDelete();

  const isClosed = settlement?.driver_settlement_status === 'CLOSED' || Boolean(settlement?.money_collected_at);

  const handleChangeStatus = async () => {
    if (!settlement?.id) return;

    if (isClosed) {
      const confirmed = await showDeleteConfirm('Eii bạn chắc chắn chứ?', 'Phải kiểm tra kỹ khi thay đổi tình trạng của một quyết toán.');
      if (!confirmed) return;
      await markAsOpen(settlement.id);
    } else {
      await markAsClosed(settlement.id);
    }
    await refetch();
  };

  const handleDelete = async () => {
    if (!settlement?.id) return;
    const confirmed = await showDeleteConfirm(
      'Eii bạn chắc chắn chứ?',
      'Phải kiểm tra kỹ khi xóa một quyết toán. Đây là hành động không thể hoàn tác.'
    );
    if (!confirmed) return;
    await deleteSettlement(settlement.id);
    navigate('/driver_settlement');
  };

  if (isLoading) return <SkeletonPageLoading fullWidth />;

  if (isError || !settlement) {
    return (
      <Page title="Chi tiết quyết toán" backAction={{ content: 'Quay lại danh sách', onAction: () => navigate('/driver_settlement') }}>
        <Banner tone="critical" title="Không thể tải quyết toán">
          <p>Dữ liệu không tồn tại hoặc đã xảy ra lỗi khi tải. Vui lòng quay lại và thử lại.</p>
        </Banner>
      </Page>
    );
  }

  const orderEntries = Object.entries(settlement.order_snapshot || {});
  const orderFeeEntries = Object.entries(settlement.order_fee_snapshot || {}).flatMap(([orderCode, fees]) =>
    Object.entries(fees).map(([feeKey, value]) => ({ orderCode, feeKey, value }))
  );
  const tripFeeEntries = Object.entries(settlement.trip_manifest_fee_snapshot || {});
  const feeLabel = (key: string, type: 'order' | 'trip') => {
    const labels = type === 'order' ? ORDER_FEE_LABEL : TRIP_FEE_LABELS;
    return labels[key as keyof typeof labels] || key;
  };

  return (
    <>
      <Helmet>
        <title>Quyết toán {settlement.id}</title>
      </Helmet>
      <Page
        fullWidth
        title={`Quyết toán #${settlement.id || '--'}`}
        subtitle={`${settlement.trip_name || 'Chưa có tên chuyến'} · ${settlement.license_plate || 'Chưa có biển số'}`}
        titleMetadata={<Badge tone={isClosed ? 'success' : 'critical'}>{isClosed ? 'Đã đóng' : 'Đang mở'}</Badge>}
        backAction={{ content: 'Quay lại danh sách', onAction: () => navigate('/driver_settlement') }}
      >
        <BlockStack gap="500">
          <InlineGrid columns={{ xs: 1, sm: 3, md: 5 }} gap="400">
            <Card>
              <BlockStack gap="150">
                <Text as="span" tone="subdued">
                  Tổng tiền vé
                </Text>
                <Text as="p" variant="headingLg">
                  {formatMoney(settlement.total_fare)}
                </Text>
              </BlockStack>
            </Card>
            <Card>
              <BlockStack gap="150">
                <Text as="span" tone="subdued">
                  Chi phí
                </Text>
                <Text as="p" variant="headingLg">
                  {formatMoney(settlement.total_fee)}
                </Text>
              </BlockStack>
            </Card>
            <Card>
              <BlockStack gap="150">
                <Text as="span" tone="subdued">
                  Tài xế Phải thu
                </Text>
                <Text as="p" variant="headingLg">
                  {formatMoney(settlement.total_due)}
                </Text>
              </BlockStack>
            </Card>
            <Card>
              <BlockStack gap="150">
                <Text as="span" tone="subdued">
                  Nộp lại
                </Text>
                <Text as="p" variant="headingLg">
                  {formatMoney(settlement.total_settlement_amount)}
                </Text>
              </BlockStack>
            </Card>
            <Card>
              <BlockStack gap="150">
                <Text as="span" tone="subdued">
                  Lương + thưởng
                </Text>
                <Text as="p" variant="headingLg">
                  {formatMoney(settlement.driver_earning_amount)}
                </Text>
              </BlockStack>
            </Card>
          </InlineGrid>

          <Layout>
            <Layout.Section>
              <BlockStack gap="400">
                <Card>
                  <BlockStack gap="400">
                    <SectionTitle>Thông tin chuyến</SectionTitle>
                    <Divider />
                    <DescriptionList
                      gap="loose"
                      items={[
                        {
                          term: 'Tài xế',
                          description: `${settlement.driver_data?.display_name || '--'} / ${settlement.driver_data?.user_phonenumber || '--'}`,
                        },
                        { term: 'Hành trình', description: settlement.trip_name || '--' },
                        { term: 'Biển số xe', description: settlement.license_plate || '--' },
                        { term: 'Ngày giờ đi', description: formatDate(settlement.trip_departure_date) },
                        { term: 'Số ghế / hàng', description: `${settlement.total_seats || 0} ghế / ${settlement.total_goods || 0} hàng` },
                        {
                          term: 'Mã chuyến',
                          description: (
                            <Link
                              url={`/ticket_managers?d=${dayjs(Number(settlement.trip_manifest?.departure_date)).tz('Asia/Ho_Chi_Minh').format('YYYY-MM-DD')}&journey_id=${settlement?.journey_id}&h=${settlement?.trip_manifest?.time_id}`}
                            >
                              Đi tới chuyến {settlement.trip_manifest_id || '--'}
                            </Link>
                          ),
                        },
                      ]}
                    />
                  </BlockStack>
                </Card>

                <Card>
                  <BlockStack gap="400">
                    <SectionTitle>Chi tiết vé</SectionTitle>
                    <Divider />
                    {orderEntries.length ? (
                      <InlineGrid columns={{ xs: 1, md: 2 }} gap="300">
                        {orderEntries.map(([orderCode, order]) => (
                          <Box key={orderCode} padding="300" background="bg-surface-secondary" borderRadius="300">
                            <BlockStack gap="200">
                              <Text as="h3" variant="headingSm">
                                {orderCode}
                              </Text>
                              <DescriptionList
                                gap="tight"
                                items={[
                                  { term: 'Khách hàng', description: order?.customer_information ?? '--' },
                                  { term: 'Tiền vé', description: formatMoney(order.total_must_pay) },
                                  { term: 'Tổng phí', description: formatMoney(order.total_fee) },
                                  {
                                    term: 'Danh sách Phí',
                                    description: order.order_fee?.length ? (
                                      <Box padding="200" background="bg-surface" borderRadius="200">
                                        {order.order_fee.map((fee, index) => (
                                          <InlineStack align="space-between" blockAlign="center" gap={'200'}>
                                            <Text as="p" variant="bodyMd" fontWeight="semibold">
                                              {feeLabel(fee.fee_description || '', 'order')}
                                            </Text>
                                            <Text as="p" tone="subdued">
                                              {formatMoney(fee.fee_value)}
                                            </Text>
                                          </InlineStack>
                                        ))}
                                      </Box>
                                    ) : (
                                      <Text as="p" tone="subdued">
                                        --
                                      </Text>
                                    ),
                                  },
                                  {
                                    term: 'Điểm đón',
                                    description: (
                                      <>
                                        <Text as="p">{order.pickup_address || '--'}</Text>
                                        <Text as="p" tone="critical">
                                          {order.pickup_note || '--'}
                                        </Text>
                                      </>
                                    ),
                                  },
                                  {
                                    term: 'Điểm trả',
                                    description: (
                                      <>
                                        <Text as="p">{order.dropoff_address || '--'}</Text>
                                        <Text as="p" tone="critical">
                                          {order.dropoff_note || '--'}
                                        </Text>
                                      </>
                                    ),
                                  },
                                  {
                                    term: 'TT thanh toán',
                                    description: order?.payment_information?.map((transaction) => {
                                      return (
                                        <>
                                          <Text as="p" fontWeight="bold">
                                            {transaction.payment_method} : {__helpers.formatNumberWithCommas(transaction.amount || 0)} vnd
                                          </Text>
                                          <Text as="p" variant="bodyXs">
                                            {transaction.description}
                                          </Text>
                                        </>
                                      );
                                    }), //
                                  },
                                ]}
                              />
                            </BlockStack>
                          </Box>
                        ))}
                      </InlineGrid>
                    ) : (
                      <Text as="p" tone="subdued">
                        Không có dữ liệu vé.
                      </Text>
                    )}
                  </BlockStack>
                </Card>

                <InlineGrid columns={{ xs: 1, md: 2 }} gap="400">
                  <Card>
                    <BlockStack gap="400">
                      <SectionTitle>Phí vé</SectionTitle>
                      <Divider />
                      <DescriptionList
                        gap="tight"
                        items={
                          orderFeeEntries.length
                            ? orderFeeEntries.map(({ orderCode, feeKey, value }) => ({
                                term: `${orderCode} · ${feeLabel(feeKey, 'order')}`,
                                description: formatMoney(value),
                              }))
                            : [{ term: '--', description: 'Không có dữ liệu phí vé' }]
                        }
                      />
                    </BlockStack>
                  </Card>
                  <Card>
                    <BlockStack gap="400">
                      <SectionTitle>Phí chuyến</SectionTitle>
                      <Divider />
                      <DescriptionList
                        gap="tight"
                        items={
                          tripFeeEntries.length
                            ? tripFeeEntries.map(([key, value]) => ({
                                term: feeLabel(key, 'trip'),
                                description: formatMoney(value),
                              }))
                            : [{ term: '--', description: 'Không có dữ liệu phí chuyến' }]
                        }
                      />
                    </BlockStack>
                  </Card>
                </InlineGrid>
              </BlockStack>
            </Layout.Section>

            <Layout.Section variant="oneThird">
              <BlockStack gap="400">
                <Card>
                  <BlockStack gap="400">
                    <SectionTitle>Tài chính</SectionTitle>
                    <Divider />
                    <DescriptionList
                      gap="tight"
                      items={[
                        { term: 'Tổng tiền vé', description: formatMoney(settlement.total_fare) },
                        { term: 'Tổng phí', description: formatMoney(settlement.total_fee) },
                        { term: 'Tài xế thu', description: formatMoney(settlement.total_due) },
                        { term: 'Tổng quyết toán', description: formatMoney(settlement.total_settlement_amount) },
                        { term: 'Lương / thưởng tài xế', description: formatMoney(settlement.driver_earning_amount) },
                        { term: 'Giải trình lương', description: settlement.driver_earning_snapshot?.calculation?.formula || '--' },
                      ]}
                    />
                  </BlockStack>
                </Card>
                <Card>
                  <BlockStack gap="400">
                    <SectionTitle>Thông tin phiếu</SectionTitle>
                    <Divider />
                    <DescriptionList
                      gap="tight"
                      items={[
                        { term: 'Mã quyết toán', description: settlement.id || '--' },
                        { term: 'Ngày tạo phiếu', description: formatDate(settlement.createdAt) },
                        { term: 'Người thu tiền', description: settlement.money_collector?.display_name || '--' },
                        { term: 'Ngày thu tiền', description: formatDate(settlement.money_collected_at) },
                      ]}
                    />
                  </BlockStack>
                </Card>
                <Card>
                  <BlockStack gap="300">
                    <SectionTitle>Thao tác</SectionTitle>
                    <Divider />
                    <Button
                      fullWidth
                      icon={isClosed ? ProductUnavailableIcon : StatusActiveIcon}
                      tone={isClosed ? undefined : 'success'}
                      loading={isOpening || isClosing}
                      onClick={handleChangeStatus}
                    >
                      {isClosed ? 'Mở lại quyết toán' : 'Xác nhận thu tiền'}
                    </Button>
                    <Capacity current_user_can="super_admin" onFail={null}>
                      <Button fullWidth tone="critical" icon={DeleteIcon} loading={isDeleting} onClick={handleDelete}>
                        Xóa quyết toán
                      </Button>
                    </Capacity>
                  </BlockStack>
                </Card>
              </BlockStack>
            </Layout.Section>
          </Layout>
        </BlockStack>
      </Page>
      <br />
      <br />
      <br />
    </>
  );
}

